import { useMemo } from "react";
import type { ColumnDef } from "@repo/ui";
import { cn } from "@repo/utils";
import type { CustomerField, Workflow } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";

type Props = {
  customerFields: CustomerField[];
  workflows: Workflow[];
};

export const getActiveLabel = (isActive: boolean): string => (isActive ? "Active" : "Inactive");

export const useTaskColumns = ({ customerFields, workflows }: Props) => {
  const dynamicColumns = useMemo<ColumnDef<TaskManagerDTO>[]>(() => {
    const workflowById = new Map(workflows.map((workflow) => [workflow.id, workflow]));

    const baseColumns: ColumnDef<TaskManagerDTO>[] = [
      {
        key: "name",
        header: "Task Name",
        width: "flex-2",
        sortable: true,
        searchable: true,
      },
      {
        key: "workflowId",
        header: "Workflow",
        width: "flex-1",
        sortable: true,
        searchable: true,
        render: (_value, row) => {
          const taskWorkflow = workflowById.get(row.workflowId);
          return taskWorkflow?.name || "-";
        },
        searchValue: (row) => {
          const taskWorkflow = workflowById.get(row.workflowId);
          return taskWorkflow?.name || "";
        },
      },
      {
        key: "active",
        header: "Active",
        width: "w-24",
        sortable: true,
        searchable: true,
        searchValue: (row) => getActiveLabel(row.active),
        render: (value) => (
          <span
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium",
              value ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
            )}
          >
            {getActiveLabel(!!value)}
          </span>
        ),
      },
    ];

    if (!customerFields || customerFields.length === 0) {
      return baseColumns;
    }

    const fieldColumns: ColumnDef<TaskManagerDTO>[] = customerFields.map((customerField) => ({
      key: ("taskFields" + customerField.id) as keyof TaskManagerDTO,
      header: customerField.name,
      width: "flex-1",
      sortable: false,
      searchable: true,
      render: (_value, row) => {
        const taskWorkflow = workflowById.get(row.workflowId);

        if (!taskWorkflow || !taskWorkflow.fields) {
          return "-";
        }

        const matchingWorkflowField = taskWorkflow.fields.find(
          (wf) => wf.name === customerField.name && wf.type === customerField.type
        );

        if (!matchingWorkflowField) {
          return "-";
        }

        const taskField = row.taskFields?.find((tf) => tf.fieldId === matchingWorkflowField.id);

        return taskField?.value !== undefined && taskField?.value !== null
          ? String(taskField.value)
          : "-";
      },
      searchValue: (row) => {
        const taskWorkflow = workflowById.get(row.workflowId);
        if (!taskWorkflow?.fields) return "";

        const matchingWorkflowField = taskWorkflow.fields.find(
          (wf) => wf.name === customerField.name && wf.type === customerField.type
        );
        if (!matchingWorkflowField) return "";

        const value = row.taskFields?.find((tf) => tf.fieldId === matchingWorkflowField.id)?.value;

        return value !== undefined && value !== null ? String(value) : "";
      },
    }));

    return [...baseColumns, ...fieldColumns];
  }, [customerFields, workflows]);

  return dynamicColumns;
};
